import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { PropsWithChildren, useEffect } from 'react';
import styled from 'styled-components';
import { cn } from '~/lib/cn';
import Dropdown from './ui/Dropdown';

const TabBarWrapper = styled.div`
    a.active {
        font-weight: 400;
        color: #000 !important;
    }

    .nav-pills .active {
        background-color: ${(p) => p.theme.colors.light};
    }
`;

export interface TabBarSection {
    key: string;
    title: string;
    content: React.ElementType | string | null;
    active?: boolean;
    withLink?: boolean;
    show?: boolean;
}

interface Props {
    label?: string;
    sections: TabBarSection[];
    column?: boolean;
    ddClassName?: string;
    className?: string;
    urlKey?: string;
    getSectionIndex?: (number?: number) => void;
    breakPoint?: 'sm' | 'md' | 'lg' | 'xl';
}

export const TabBar = ({
    label = 'Mehr',
    sections,
    breakPoint = 'sm',
    column = false,
    ddClassName,
    className,
    urlKey = 'tab',
    getSectionIndex = () => {},
    ...props
}: Props) => {
    const { query } = useRouter();
    const key = query[urlKey] || sections[0]?.key;

    useEffect(() => {
        let index = sections.findIndex((section) => {
            if (!key) {
                return !section.key || section.key === '';
            } else {
                return section.key === key;
            }
        });

        getSectionIndex(index === -1 ? 0 : index);
    }, [key, sections]);

    const DropdownItem = React.forwardRef(
        ({ children, ...props }: PropsWithChildren<{}>, ref: React.Ref<HTMLDivElement>) => (
            <div ref={ref} {...props}>
                {children}
            </div>
        )
    );
    DropdownItem.displayName = 'DropdownItem';

    return (
        <TabBarWrapper className={className}>
            <div className={`d-${breakPoint}-none d-block`}>
                <Dropdown size="md" label={label} className={ddClassName}>
                    {sections
                        .filter((s) => s.withLink !== false && s.show !== false)
                        .map(
                            (section) =>
                                (typeof section.show === 'undefined' || section.show) && (
                                    <Dropdown.Item key={section.key}>
                                        <TabBarLink urlKey={urlKey} section={section} active={section.key === key} />
                                    </Dropdown.Item>
                                )
                        )}
                </Dropdown>
            </div>

            <div className={`d-${breakPoint}-block d-none`}>
                <nav className={cn('tw-flex tw-flex-wrap')} {...props}>
                    {sections
                        .filter((s) => s.withLink !== false && s.show !== false)
                        .map(
                            (section, index) =>
                                (typeof section.show === 'undefined' || section.show) && (
                                    <TabBarLink
                                        key={section.key}
                                        urlKey={urlKey}
                                        section={section}
                                        active={(!key && section.key === '') || section.key === key}
                                    />
                                )
                        )}
                </nav>
            </div>
        </TabBarWrapper>
    );
};

export const TabBarLink = ({ section, active = false, urlKey }) => {
    const { pathname, asPath } = useRouter();
    let query = asPath.includes('?')
        ? asPath
              .substring(asPath.indexOf('?') + 1, asPath.length)
              .split('&')
              .reduce((obj, pair) => {
                  const pairArr = pair.split('=');
                  obj[pairArr[0]] = pairArr[0] === urlKey ? section.key : pairArr[1];
                  return obj;
              }, {})
        : {};

    if (!query[urlKey]) {
        query[urlKey] = section.key;
    }

    const queryString = Object.entries(query)
        .map((pair) => pair.join('='))
        .join('&');
    const path = `${asPath.includes('?') ? asPath.substring(0, asPath.indexOf('?')) : asPath}?${queryString}`;

    return section.active === false ? null : (
        <Link
            scroll={false}
            href={section.link || { pathname: pathname, query }}
            as={section.link || section.linkAs || path}
            shallow={true}
            className={'nav-link text-secondary ' + (active && 'active')}
        >
            {section.title}
        </Link>
    );
};
